import React, { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Typography,
} from "@mui/joy";
import CustomModal from "components/common/CustomModal";

export interface TaskMetadataValues {
  taskId: string;
  title: string;
  setNumber?: number;
  isKeyActivity: boolean;
}

interface TaskMetadataModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialValues?: TaskMetadataValues;
  existingTaskIds: string[];
  excludeTaskId?: string;
  onClose: () => void;
  onSubmit: (values: TaskMetadataValues) => void;
}

const EMPTY_VALUES: TaskMetadataValues = {
  taskId: "",
  title: "",
  setNumber: undefined,
  isKeyActivity: false,
};

export default function TaskMetadataModal({
  open,
  mode,
  initialValues,
  existingTaskIds,
  excludeTaskId,
  onClose,
  onSubmit,
}: TaskMetadataModalProps) {
  const [values, setValues] = useState<TaskMetadataValues>(EMPTY_VALUES);
  const [setNumberText, setSetNumberText] = useState("");
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      return;
    }
    const next = initialValues ?? EMPTY_VALUES;
    setValues(next);
    setSetNumberText(
      typeof next.setNumber === "number" ? String(next.setNumber) : ""
    );
    setError(undefined);
  }, [open, initialValues]);

  const taskIdSet = useMemo(
    () =>
      new Set(
        existingTaskIds.map((id) => id.trim()).filter((id) => id.length > 0)
      ),
    [existingTaskIds]
  );

  const handleSubmit = () => {
    const trimmedTaskId = values.taskId.trim();
    if (!trimmedTaskId) {
      setError("ID 를 입력해주세요");
      return;
    }

    const excludedTaskId = excludeTaskId?.trim();
    if (taskIdSet.has(trimmedTaskId) && trimmedTaskId !== excludedTaskId) {
      setError(`ID "${trimmedTaskId}" 는 이미 사용 중입니다`);
      return;
    }

    let nextSetNumber: number | undefined;
    const trimmedSetNumberText = setNumberText.trim();
    if (trimmedSetNumberText.length > 0) {
      const parsed = Number.parseInt(trimmedSetNumberText, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setError("세트 번호는 양의 정수여야 합니다");
        return;
      }
      nextSetNumber = parsed;
    }

    onSubmit({
      taskId: trimmedTaskId,
      title: values.title.trim(),
      setNumber: nextSetNumber,
      isKeyActivity: values.isKeyActivity,
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={mode === "create" ? "새 활동지" : "활동지 메타데이터"}
      subtitle={
        mode === "create"
          ? "활동지의 기본 정보를 입력하세요"
          : "이 활동지의 메타데이터를 수정합니다"
      }
      actions={[
        {
          label: mode === "create" ? "만들기" : "저장",
          onClick: handleSubmit,
          variant: "Solid",
        },
        {
          label: "취소",
          onClick: onClose,
          variant: "Plain",
        },
      ]}
    >
      <Stack spacing={2}>
        <FormControl>
          <FormLabel>ID</FormLabel>
          <Input
            value={values.taskId}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, taskId: event.target.value }))
            }
            placeholder="예: 1-1-A"
            autoFocus={mode === "create"}
          />
        </FormControl>

        <FormControl>
          <FormLabel>제목</FormLabel>
          <Input
            value={values.title}
            onChange={(event) =>
              setValues((prev) => ({ ...prev, title: event.target.value }))
            }
            placeholder="예: 우울감 알아차리기"
          />
        </FormControl>

        <FormControl>
          <FormLabel>세트 번호 (선택)</FormLabel>
          <Input
            type="number"
            value={setNumberText}
            onChange={(event) => setSetNumberText(event.target.value)}
            placeholder="예: 1"
          />
        </FormControl>

        <FormControl orientation="horizontal">
          <Checkbox
            label="Key 활동지"
            checked={values.isKeyActivity}
            onChange={(event) =>
              setValues((prev) => ({
                ...prev,
                isKeyActivity: event.target.checked,
              }))
            }
          />
        </FormControl>

        {error && (
          <Typography level="body-sm" color="danger">
            {error}
          </Typography>
        )}
      </Stack>
    </CustomModal>
  );
}
